import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import type { InstalledApp } from '../../hooks/useAppSystem';
import { useBusiness } from '../../contexts/BusinessContext';
import { useInventoryMovementMode } from '../../hooks/useInventoryMovementMode';
import { useInventoryConsumedReport } from '../../hooks/useInventoryConsumedReport';
import { useInventoryPurchasedReport } from '../../hooks/useInventoryPurchasedReport';
import { useInventorySalesReport } from '../../hooks/useInventorySalesReport';
import { useInventorySnippetData } from '../../hooks/useInventorySnippetData';
import { usePurchaseOrdersSnippetData } from '../../hooks/usePurchaseOrdersSnippetData';
import { getOnOrderQuantities } from '../../lib/purchaseOrders';
import { getStockItems, type StockItem } from '../../lib/stockItems';
import CreateOrderModal from '../CreateOrderModal';
import PlaceOrderModal from '../PlaceOrderModal';
import PurchaseOrderDetailModal from '../PurchaseOrderDetailModal';import DashboardAppSnippet from './DashboardAppSnippet';
import InventoryHeaderMetrics from './InventoryHeaderMetrics';
import InventoryLowStockPanel from './InventoryLowStockPanel';
import InventoryMovementByCategoryPanel from './InventoryMovementByCategoryPanel';
import InventoryMovementModeToggle from './InventoryMovementModeToggle';
import InventoryPurchaseOrdersPanel from './InventoryPurchaseOrdersPanel';

interface InventoryDashboardSnippetProps {
  app: InstalledApp;
}

/** Control-centre panel for the Inventory app — reports + PO / low-stock sidebars. */
export default function InventoryDashboardSnippet({ app }: InventoryDashboardSnippetProps) {
  const { selectedBusiness } = useBusiness();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
  const [isPlaceOrderModalOpen, setIsPlaceOrderModalOpen] = useState(false);  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [onOrderQuantities, setOnOrderQuantities] = useState<Record<string, number>>({});

  const { mode: movementMode, setMode: setMovementMode } = useInventoryMovementMode();
  const { metrics, lowStockItems, loading, error, refresh } = useInventorySnippetData(movementMode);
  const {
    categories: salesCategories,
    totalAmount: salesTotalAmount,
    loading: salesLoading,
    error: salesError,
    refresh: refreshSales,
  } = useInventorySalesReport(movementMode === 'sales');
  const {
    categories: consumedCategories,
    totalAmount: consumedTotalAmount,
    loading: consumedLoading,
    error: consumedError,
    refresh: refreshConsumed,
  } = useInventoryConsumedReport(movementMode === 'consumed');
  const {
    categories: purchasedCategories,
    totalAmount: purchasedTotalAmount,
    loading: purchasedLoading,
    error: purchasedError,
    refresh: refreshPurchased,
  } = useInventoryPurchasedReport();
  const {
    orders,
    purchaseOrders,
    pendingCount,
    loading: ordersLoading,
    error: ordersError,
    refresh: refreshOrders,
  } = usePurchaseOrdersSnippetData();

  const selectedOrder = useMemo(
    () => (selectedOrderId ? orders.find((order) => order.id === selectedOrderId) ?? null : null),
    [orders, selectedOrderId]
  );

  useEffect(() => {
    if (!selectedOrderId) return;
    const order = orders.find((o) => o.id === selectedOrderId);
    if (!order || order.status !== 'pending') {
      setSelectedOrderId(null);
    }
  }, [orders, selectedOrderId]);

  const movementCategories = movementMode === 'sales' ? salesCategories : consumedCategories;
  const movementTotalAmount = movementMode === 'sales' ? salesTotalAmount : consumedTotalAmount;
  const movementLoading = movementMode === 'sales' ? salesLoading : consumedLoading;
  const movementError = movementMode === 'sales' ? salesError : consumedError;
  const refreshMovement = movementMode === 'sales' ? refreshSales : refreshConsumed;

  const reloadStockItemsForOrder = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    const quantities = await getOnOrderQuantities(selectedBusiness.id);
    setOnOrderQuantities(quantities);
    const items = await getStockItems(selectedBusiness.id, {
      includePendingOneTimeItems: true,
      onOrderQuantities: quantities,
    });
    setStockItems(items);
  }, [selectedBusiness?.id]);

  const handleOpenCreateOrder = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    try {
      await reloadStockItemsForOrder();
      setIsCreateOrderModalOpen(true);
    } catch (err) {
      console.error('Error loading stock for create order:', err);
    }
  }, [reloadStockItemsForOrder, selectedBusiness?.id]);

  const handleOpenQuickOrder = useCallback(async () => {
    if (!selectedBusiness?.id) return;
    try {
      await reloadStockItemsForOrder();
      setIsPlaceOrderModalOpen(true);
    } catch (err) {
      console.error('Error loading stock for quick order:', err);
    }
  }, [reloadStockItemsForOrder, selectedBusiness?.id]);

  const handlePlaceOrderClose = useCallback(() => {
    setIsPlaceOrderModalOpen(false);
    void Promise.all([refreshOrders(), refresh()]);
  }, [refresh, refreshOrders]);

  const handleOrderCreated = useCallback(async () => {
    await Promise.all([refreshOrders(), refreshPurchased(), refresh()]);
  }, [refresh, refreshOrders, refreshPurchased]);

  return (
    <>
      <DashboardAppSnippet
        app={app}
        appRoute="/stock"
        compactMobileHeader
        headerBesideIcon={
          <InventoryMovementModeToggle mode={movementMode} onModeChange={setMovementMode} />
        }
        headerActions={
          <Link
            to="/stock"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.96] touch-manipulation sm:h-9 sm:w-9"
            aria-label="Open inventory"
            title="Open inventory"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Plus className="h-5 w-5" strokeWidth={2.5} />
          </Link>
        }
        headerEnd={
          <InventoryHeaderMetrics
            metrics={metrics}
            movementMode={movementMode}
            loading={loading}
          />
        }
      >
        <div className="flex w-full flex-col -mx-1 min-h-[min(420px,55vh)]">
          <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden sm:gap-3 lg:h-[min(380px,48vh)] lg:flex-row lg:items-stretch">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2 overflow-hidden sm:flex-row sm:gap-3">
              <InventoryMovementByCategoryPanel
                variant={movementMode}
                categories={movementCategories}
                totalAmount={movementTotalAmount}
                loading={movementLoading}
                error={movementError}
                onRetry={() => void refreshMovement()}
              />
              <InventoryMovementByCategoryPanel
                variant="purchased"
                categories={purchasedCategories}
                totalAmount={purchasedTotalAmount}
                loading={purchasedLoading}
                error={purchasedError}
                onRetry={() => void refreshPurchased()}
              />
            </div>

            <div className="flex min-h-0 w-full shrink-0 flex-col gap-2 overflow-hidden lg:w-auto lg:flex-row lg:gap-3">
              <div className="flex h-full min-h-0 w-full flex-col overflow-hidden lg:w-[min(100%,280px)] xl:w-[300px]">
                <InventoryPurchaseOrdersPanel
                  orders={purchaseOrders}
                  pendingCount={pendingCount}
                  loading={ordersLoading}
                  error={ordersError}
                  onRetry={() => void refreshOrders()}
                  onOrderClick={setSelectedOrderId}
                  onCreateOrder={() => void handleOpenCreateOrder()}
                />
              </div>

              <div className="flex h-full min-h-0 w-full flex-col overflow-hidden lg:w-[min(100%,280px)] xl:w-[300px]">
                <InventoryLowStockPanel
                  layout="sidebar"
                  items={lowStockItems}
                  loading={loading}
                  error={error}
                  onRetry={() => void refresh()}
                  onQuickOrder={() => void handleOpenQuickOrder()}
                />              </div>
            </div>
          </div>
        </div>
      </DashboardAppSnippet>

      <PurchaseOrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrderId(null)}
        onOrderUpdated={() => void refreshOrders()}
        business={
          selectedBusiness
            ? {
                name: selectedBusiness.name,
                address: selectedBusiness.address ?? null,
                phone: selectedBusiness.phone ?? null,
                email: selectedBusiness.email ?? null,
              }
            : null
        }
      />

      <PlaceOrderModal
        isOpen={isPlaceOrderModalOpen}
        onClose={handlePlaceOrderClose}
        items={stockItems}
      />

      <CreateOrderModal        isOpen={isCreateOrderModalOpen}
        onClose={() => setIsCreateOrderModalOpen(false)}
        items={stockItems}
        onOrderQuantities={onOrderQuantities}
        onItemsRefresh={() => void reloadStockItemsForOrder()}
        onOrderCreated={handleOrderCreated}
      />
    </>
  );
}
